/**
 * Abgelaufene Ware — abgeleitete Ansicht aus Lots (kein separates Wahrheitsmodell).
 * ADR-0013 Layout: öffentliche API = domain/inventory/expiredView.
 */

import { LOT_STATUS, isLotExpired } from '../../../utils/lotSystem';
import { sameProductId } from '../../../utils/productIdUtils';
import { kvGet } from '../../../infrastructure/storage/kvAccess';

function parseKvJson(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isDeletedExpiredMarker(productId, expirationDate, location) {
  const deletedKey = `deleted_expired_${productId}_${expirationDate}_${location}`;
  const raw = kvGet(deletedKey);
  return raw != null && raw !== '' && parseKvJson(raw);
}

function resolveExpiredQuantity(lot) {
  const processedKey = `processed_expired_lot_${lot.id}`;
  const processed = parseKvJson(kvGet(processedKey));
  const fromProcessed = Number(processed?.quantity) || 0;
  if (fromProcessed > 0) return fromProcessed;

  const fromLotField = Number(lot.expiredQuantity) || 0;
  if (fromLotField > 0) return fromLotField;

  // Pending sweep: still ACTIVE but past expiry — show remaining as Verlust-Menge
  if (lot.status !== LOT_STATUS.EXPIRED && isLotExpired(lot)) {
    return Number(lot.remainingQuantity) || 0;
  }

  return 0;
}

function isExpiredLotForReport(lot) {
  if (!lot || typeof lot !== 'object') return false;
  if (!lot.expirationDate) return false;

  const loc = lot.location || 'Principal';
  if (isDeletedExpiredMarker(lot.productId, lot.expirationDate, loc)) return false;

  if (lot.status === LOT_STATUS.EXPIRED) return true;

  // Date-expired with remaining stock not yet swept → still show as Verlust
  if (isLotExpired(lot) && Number(lot.remainingQuantity) > 0) {
    if (lot.status && lot.status !== LOT_STATUS.ACTIVE) return false;
    return true;
  }

  return false;
}

/**
 * Report-Zeilen aus abgelaufenen Lots (status EXPIRED oder kalendarisch abgelaufen).
 * @param {object[]} lots
 * @param {object[]} products
 */
export function deriveExpiredProductsFromLots(lots = [], products = []) {
  const list = Array.isArray(lots) ? lots : [];
  const productList = Array.isArray(products) ? products : [];

  return list.filter(isExpiredLotForReport).map((lot) => {
    const product = productList.find((p) =>
      sameProductId(p.id ?? p.productId, lot.productId)
    );
    const expiredQty = resolveExpiredQuantity(lot);
    const pendingSweep =
      lot.status !== LOT_STATUS.EXPIRED && isLotExpired(lot) && Number(lot.remainingQuantity) > 0;

    return {
      id: lot.id,
      lotId: lot.id,
      productId: lot.productId,
      productName: product?.name || lot.expiredProductName || '',
      category: product?.category,
      subcategory: product?.subcategory,
      type: product?.type,
      quantity: expiredQty,
      expirationDate: lot.expirationDate,
      purchaseDate: lot.purchaseDate,
      stockLocation: lot.location || 'Principal',
      batchNumber: lot.batchNumber,
      purchaseUnitPrice: lot.purchaseUnitPrice,
      sellingUnitPrice: lot.sellingUnitPrice,
      expiredAt: lot.expiredAt || lot.updatedAt || lot.createdAt,
      status: 'EXPIRED',
      lossType: 'expiration',
      pendingSweep: Boolean(pendingSweep),
      derivedFromLot: true
    };
  });
}

/**
 * Legacy-Array ∪ abgeleitete Lots (Lot gewinnt bei gleicher lotId).
 */
export function mergeExpiredProductsForDisplay(legacyExpired = [], lots = [], products = []) {
  const derived = deriveExpiredProductsFromLots(lots, products);
  const byLotId = new Map();

  for (const row of legacyExpired || []) {
    if (row?.lotId) byLotId.set(String(row.lotId), row);
  }
  for (const row of derived) {
    byLotId.set(String(row.lotId), row);
  }
  return Array.from(byLotId.values()).sort((a, b) => {
    const ad = new Date(a.expirationDate || 0);
    const bd = new Date(b.expirationDate || 0);
    return bd - ad;
  });
}
